import React, { useState, useEffect } from 'react';
import './AppStyles.css';
import { useTranslation } from 'react-i18next';
import fertilizerAdvice from './data/fertilizerAdvice';
import ImageDiagnosis from './components/ImageDiagnosis';
import FeedbackForm from './components/FeedbackForm';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

serviceWorkerRegistration.register();

const AgroAssistApp = () => {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState('crop');
  const [selectedCrop, setSelectedCrop] = useState('');
  const [selectedSoil, setSelectedSoil] = useState('');

  const crops = [
    { name: 'Maize', planting: 'April - June', spacing: '75cm x 25cm', fertilizer: 'NPK 20:10:10' },
    { name: 'Cassava', planting: 'March - May', spacing: '1m x 1m', fertilizer: 'Organic manure preferred' },
    { name: 'Rice', planting: 'May - July', spacing: '20cm x 20cm', fertilizer: 'Urea and NPK 20:10:10' },
    { name: 'Yam', planting: 'February - April', spacing: '1m x 1m', fertilizer: 'Organic compost or NPK 15:15:15' },
    { name: 'Tomato', planting: 'October - December', spacing: '60cm x 45cm', fertilizer: 'NPK 20:10:10' },
    { name: 'Groundnut', planting: 'May - June', spacing: '30cm x 15cm', fertilizer: 'SSP and NPK 15:15:15' },
    { name: 'Sorghum', planting: 'June - August', spacing: '75cm x 20cm', fertilizer: 'NPK 20:10:10' },
    { name: 'Millet', planting: 'June - July', spacing: '60cm x 15cm', fertilizer: 'NPK 15:15:15' },
    { name: 'Cocoa', planting: 'April - June', spacing: '3m x 3m', fertilizer: 'Compost or NPK 20:10:10' },
    { name: 'Oil Palm', planting: 'April - May', spacing: '9m x 9m', fertilizer: 'NPKMg 12:12:17 + 2MgO' }
  ];

  const livestock = [
    { name: 'Goat', feeding: 'Grass, grains', housing: 'Elevated wooden house', vaccination: 'PPR, Enterotoxaemia' },
    { name: 'Chicken', feeding: 'Starter mash, grower mash', housing: 'Deep litter system', vaccination: 'Newcastle, Gumboro' },
    { name: 'Sheep', feeding: 'Pasture, hay, and grains', housing: 'Ventilated shed with bedding', vaccination: 'Enterotoxemia, Sheep pox' },
    { name: 'Cattle', feeding: 'Grass, silage, and protein supplements', housing: 'Open-sided barn with clean floor', vaccination: 'Anthrax, Brucellosis, FMD' },
    { name: 'Turkey', feeding: 'Poultry feed, greens, and grains', housing: 'Spacious, dry and warm coop', vaccination: 'Fowl pox, Newcastle' },
    { name: 'Rabbit', feeding: 'Pellets, green vegetables, hay', housing: 'Wire mesh cages with roofing', vaccination: 'Myxomatosis, Rabbit Hemorrhagic Disease' },
    { name: 'Duck', feeding: 'Wet mash, insects, and greens', housing: 'Water access with dry shelter', vaccination: 'Duck viral hepatitis, Cholera' },
    { name: 'Pig', feeding: 'Balanced swine feed, maize, bran', housing: 'Concrete pen with good drainage', vaccination: 'Swine fever, Erysipelas' },
    { name: 'Quail', feeding: 'Starter mash, ground grains', housing: 'Ventilated cages with sawdust', vaccination: 'Newcastle, Fowl cholera' },
    { name: 'Guinea Fowl', feeding: 'Grains, greens, insects', housing: 'Secure coop with roosting space', vaccination: 'Newcastle, Fowl pox' }
  ];

  // ✅ Cache data locally for offline mode
  useEffect(() => {
    localStorage.setItem('cachedCrops', JSON.stringify(crops));
    localStorage.setItem('cachedLivestock', JSON.stringify(livestock));
  }, []);

  const handleLanguageChange = (e) => {
    i18n.changeLanguage(e.target.value);
  };

  return (
    <div className="container">
      <div className="header">
        <h1>🌾 {t('welcome')}</h1>
      </div>

      <div className="language-select">
        <label htmlFor="language" style={{ marginRight: '8px' }}>{t('language')}:</label>
        <select id="language" onChange={handleLanguageChange} value={i18n.language}>
          <option value="en">English</option>
          <option value="yo">Yoruba</option>
          <option value="ig">Igbo</option>
          <option value="ff">Fulfulde</option>
          <option value="nu">Nupe</option>
          <option value="ha">Hausa</option>
        </select>
      </div>

      <div className="nav-buttons">
        <button className={view === 'crop' ? '' : 'inactive'} onClick={() => setView('crop')}>{t('Crop Guide')}</button>
        <button className={view === 'livestock' ? '' : 'inactive'} onClick={() => setView('livestock')}>{t('livestock Guide')}</button>
        <button className={view === 'soil' ? '' : 'inactive'} onClick={() => setView('soil')}>{t('Soil Checker')}</button>
        <button className={view === 'diagnosis' ? '' : 'inactive'} onClick={() => setView('diagnosis')}>
        {t('Ai Diagnosis')}
      </button>
      <button className={view === 'feedback' ? '' : 'inactive'} onClick={() => setView('feedback')}>
        {t('Contact Support')}
      </button>
      </div>

      {view === 'crop' && (
        <div className="card-grid">
          {crops.map((crop, index) => (
            <div className="card" key={index}>
              <h2>🌱 {crop.name}</h2>
              <p><strong>{t('planting')}:</strong> {crop.planting}</p>
              <p><strong>{t('spacing')}:</strong> {crop.spacing}</p>
              <p><strong>{t('fertilizer')}:</strong> {crop.fertilizer}</p>
            </div>
          ))}
        </div>
      )}

      {view === 'diagnosis' && <ImageDiagnosis />}
      {view === 'feedback' && <FeedbackForm />}

      {view === 'livestock' && (
        <div className="card-grid">
          {livestock.map((animal, index) => (
            <div className="card" key={index}>
              <h2>🐄 {animal.name}</h2>
              <p><strong>{t('feeding')}:</strong> {animal.feeding}</p>
              <p><strong>{t('housing')}:</strong> {animal.housing}</p>
              <p><strong>{t('vaccination')}:</strong> {animal.vaccination}</p>
            </div>
          ))}
        </div>
      )}

      {/* Soil Checker UI (unchanged) */}
      {view === 'soil' && (
        <div className="soil-checker">
          <h2 className="section-title">🧪 {t('soilChecker')}</h2>

          <div className="input-group">
            <label htmlFor="cropSelect">{t('selectCrop')}:</label>
            <select
              id="cropSelect"
              value={selectedCrop}
              onChange={e => {
                setSelectedCrop(e.target.value);
                setSelectedSoil('');
              }}
            >
              <option value="">{`-- ${t('chooseCrop')} --`}</option>
              {Object.keys(fertilizerAdvice).map(crop => (
                <option key={crop} value={crop}>{crop}</option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label htmlFor="soilSelect">{t('selectSoil')}:</label>
            <select
              id="soilSelect"
              value={selectedSoil}
              onChange={e => setSelectedSoil(e.target.value)}
              disabled={!selectedCrop}
            >
              <option value="">{`-- ${t('chooseSoil')} --`}</option>
              {selectedCrop &&
                fertilizerAdvice[selectedCrop] &&
                Object.keys(fertilizerAdvice[selectedCrop]).map(soil => (
                  <option key={soil} value={soil}>{soil}</option>
                ))}
            </select>
          </div>

          {selectedCrop && selectedSoil && fertilizerAdvice[selectedCrop]?.[selectedSoil] && (
            <div className="advice-box">
              <strong>{t('advice')}:</strong>
              <p>{fertilizerAdvice[selectedCrop][selectedSoil]}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgroAssistApp;
